import type { Caso, EventoProcesso, PagamentoCliente } from '../hooks/useRealtimeCaso'

// Linha do tempo do caso, derivada 100% de caso.status + eventos_processo + pagamentos.
// Sem dados novos. Função PURA (fácil de testar). Verde só com fato registrado (OAB).
export type EstadoFase = 'concluida' | 'atual' | 'futura' | 'alerta'

export interface FaseView {
  chave: string
  label: string
  descricao: string
  estado: EstadoFase
  data?: string // data do evento que concluiu a fase, quando houver
  prevista?: boolean // etapa futura condicionada ao resultado (não garantida)
}

// ordem canônica do enum caso_status (progresso)
const ORDEM: Record<string, number> = {
  triagem: 0,
  proposta_enviada: 1,
  contrato_pendente: 2,
  docs_pendentes: 3,
  em_producao: 4,
  aguardando_revisao: 5,
  protocolado: 6,
  aguardando_liminar: 7,
  liminar_deferida: 8,
  liminar_indeferida: 8,
  liminar_revogada: 8,
  em_andamento: 9,
  encerrado: 10,
  improcedente: 10,
  distratado: 10,
  arquivado: 10,
}

interface FaseDef {
  chave: string
  label: string
  descricao: string
  concluida: boolean
  data?: string
  prevista?: boolean
  alerta?: boolean
}

export function derivarTimeline(
  caso: Pick<Caso, 'status'> | null,
  eventos: Pick<EventoProcesso, 'tipo' | 'data_evento'>[],
  pagamentos: Pick<PagamentoCliente, 'tipo' | 'status'>[],
): FaseView[] {
  if (!caso) return []
  const rank = ORDEM[caso.status] ?? 0
  const dataDe = (t: string) => eventos.find((e) => e.tipo === t)?.data_evento
  const has = (t: string) => eventos.some((e) => e.tipo === t)

  const temDeferida = has('liminar_deferida')
  const temIndeferida = has('liminar_indeferida')
  const temRevogada = has('liminar_revogada')
  const temSentenca = has('sentenca')
  // RECEBIMENTO = cliente receber (NÃO é a entrada dos honorários): evento valor_recebido OU pagamento de ÊXITO recebido
  const recebeu = has('valor_recebido') || pagamentos.some((p) => p.tipo === 'exito' && p.status === 'recebido')
  const caminhoB = temIndeferida && !temDeferida // liminar negada e ainda não revertida
  const terminal = rank >= 10

  const defs: FaseDef[] = []
  defs.push({
    chave: 'contratacao',
    label: 'Contratação concluída',
    descricao: 'Você assinou a procuração e o contrato; o caso foi aceito pelo escritório.',
    concluida: rank >= ORDEM.docs_pendentes,
  })
  defs.push({
    chave: 'preparacao',
    label: 'Preparando sua ação',
    descricao: 'Reunimos seus documentos e nossa equipe jurídica redige e revisa a petição.',
    concluida: rank >= ORDEM.protocolado,
  })
  defs.push({
    chave: 'protocolo',
    label: 'Ação protocolada na Justiça',
    descricao: 'Sua ação foi ajuizada e distribuída a uma vara/juizado em Santa Catarina.',
    concluida: rank >= ORDEM.aguardando_liminar || temDeferida || temIndeferida,
  })
  defs.push({
    chave: 'analise_liminar',
    label: 'Análise do pedido de urgência',
    descricao: 'O juiz analisa o pedido para suspender suas parcelas durante o processo.',
    concluida: temDeferida || temIndeferida || rank >= 9,
    data: dataDe('liminar_deferida') ?? dataDe('liminar_indeferida'),
  })
  if (caminhoB) {
    defs.push({
      chave: 'recurso',
      label: 'Recurso da decisão',
      descricao: 'A urgência foi negada de início; recorremos para tentar reverter essa decisão.',
      concluida: temDeferida || rank >= 9,
      alerta: !temDeferida && rank < 9,
    })
  }
  defs.push({
    chave: 'suspensao',
    label: 'Parcelas suspensas',
    descricao: 'A Justiça determinou a suspensão das parcelas enquanto o processo corre.',
    concluida: temDeferida && !temRevogada,
    alerta: temRevogada,
    data: dataDe('liminar_deferida'),
  })
  defs.push({
    chave: 'andamento',
    label: 'Processo em andamento',
    descricao: 'A construtora é citada, apresenta defesa e o caso segue até a decisão.',
    concluida: temSentenca || terminal,
  })
  defs.push({
    chave: 'sentenca',
    label: 'Sentença',
    descricao: 'O juiz julga o pedido — a decisão pode ser favorável, parcial ou desfavorável.',
    concluida: temSentenca || terminal,
    data: dataDe('sentenca'),
    prevista: true,
  })
  defs.push({
    chave: 'recebimento',
    label: 'Recebimento de valores',
    descricao: 'Caso a ação seja julgada favorável, é quando os valores a que você tiver direito são pagos.',
    concluida: recebeu,
    data: dataDe('valor_recebido'),
    prevista: true,
  })

  const out: FaseView[] = []
  let atualDefinida = false
  for (const f of defs) {
    let estado: EstadoFase
    if (f.concluida) {
      estado = 'concluida'
    } else if (!atualDefinida && !terminal) {
      estado = f.alerta ? 'alerta' : 'atual'
      atualDefinida = true
    } else {
      estado = f.alerta ? 'alerta' : 'futura'
    }
    out.push({
      chave: f.chave,
      label: f.label,
      descricao: f.descricao,
      estado,
      data: f.concluida ? f.data : undefined,
      prevista: f.prevista,
    })
  }
  return out
}
